import { RefObject, useCallback, useEffect, useState } from "react";

interface UseGridColumnsOptions {
  /** Narrowest a cell may get before a column is dropped. */
  minCellWidth: number;
  /** Gap between columns, in the same units the grid uses. */
  gap: number;
  /** Used until the element has a width — including under jsdom. */
  fallback: number;
  min?: number;
  max?: number;
}

/**
 * How many columns of at least `minCellWidth` fit in `width`. The pure math
 * behind {@link useGridColumns}, shared with virtualised grids that measure
 * via their own AutoSizer instead of a ResizeObserver.
 */
export function fitGridColumns(
  width: number,
  options: UseGridColumnsOptions
): number {
  const { minCellWidth, gap, fallback, min = 2, max = 8 } = options;
  if (width <= 0) return fallback;

  const fits = Math.floor((width + gap) / (minCellWidth + gap));
  return Math.min(Math.max(fits, min), max);
}

/**
 * How many columns fit in `ref`, remeasured as it resizes.
 *
 * Grid views that can be walked with the arrow keys need the column count as a
 * number, not just as a CSS track list: `↑ ↓` step a whole row, so the count
 * the keyboard uses and the count on screen have to be the same one.
 */
export default function useGridColumns(
  ref: RefObject<HTMLElement | null>,
  options: UseGridColumnsOptions
) {
  const { minCellWidth, gap, fallback, min = 2, max = 8 } = options;
  const [columns, setColumns] = useState(fallback);

  const measure = useCallback(() => {
    const width = ref.current?.clientWidth ?? 0;
    if (width <= 0) {
      setColumns(fallback);
      return;
    }

    const fits = Math.floor((width + gap) / (minCellWidth + gap));
    setColumns(Math.min(Math.max(fits, min), max));
  }, [ref, minCellWidth, gap, fallback, min, max]);

  useEffect(() => {
    measure();

    const element = ref.current;
    if (!element || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => observer.disconnect();
  }, [measure, ref]);

  return columns;
}
